/**
 * ViewModeToggle — toggles between Grid and List view modes.
 *
 * Persists the user's preference to localStorage via the safe storage utility.
 * Falls back to List View when localStorage is empty or contains an invalid value.
 * Hidden on mobile (<768px) where List View is the default.
 * Announces mode changes via an aria-live region for screen readers.
 *
 * Does NOT reset filter, sort, or search state on toggle — only the layout changes.
 *
 * @module ViewModeToggle
 * @see Requirements 7.1, 7.4, 7.5, 7.6, 9.5
 */

import { useCallback, useRef } from 'react';
import { LayoutGrid, List } from 'lucide-react';
import { safeStorageGet, safeStorageSet } from '../utils/storage';
import type { ClientViewMode } from '../../../../types/client-workspace';
import { FOCUS_VISIBLE_CLASSES } from '../utils/accessibility';

/** localStorage key for persisting view mode across sessions */
const STORAGE_KEY = 'cw:viewMode';

/** Valid view mode values for validation */
const VALID_VIEW_MODES: ClientViewMode[] = ['list', 'grid'];

export interface ViewModeToggleProps {
  /** Current active view mode */
  value: ClientViewMode;
  /** Callback when the user switches view mode */
  onChange: (mode: ClientViewMode) => void;
}

/**
 * ViewModeToggle renders two icon buttons (List / Grid) in a button group.
 * Hidden on viewports below 768px (md breakpoint) — mobile defaults to List View.
 * Announces mode changes to screen readers via an aria-live region.
 * Preserves all filter/sort/search state on switch (only layout changes).
 */
export function ViewModeToggle({ value, onChange }: ViewModeToggleProps) {
  const announcementRef = useRef<HTMLSpanElement>(null);

  const handleModeChange = useCallback(
    (mode: ClientViewMode) => {
      if (mode === value) return;
      onChange(mode);
      safeStorageSet(
        typeof window !== 'undefined' ? window.localStorage : null,
        STORAGE_KEY,
        mode,
      );

      // Update aria-live announcement for screen readers
      if (announcementRef.current) {
        announcementRef.current.textContent = `Switched to ${mode === 'grid' ? 'Grid' : 'List'} view`;
      }
    },
    [value, onChange],
  );

  return (
    <div
      className="hidden items-center rounded-lg border border-white/10 bg-slate-950/85 p-1 md:inline-flex"
      role="group"
      aria-label="View mode"
    >
      <button
        type="button"
        onClick={() => handleModeChange('list')}
        aria-label="List view"
        aria-pressed={value === 'list'}
        className={[
          'flex items-center justify-center size-7 rounded-md transition-colors',
          FOCUS_VISIBLE_CLASSES,
          value === 'list'
            ? 'border border-sky-500/20 bg-sky-500/15 text-sky-300'
            : 'text-slate-500 hover:text-white',
        ].join(' ')}
      >
        <List className="size-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => handleModeChange('grid')}
        aria-label="Grid view"
        aria-pressed={value === 'grid'}
        className={[
          'flex items-center justify-center size-7 rounded-md transition-colors',
          FOCUS_VISIBLE_CLASSES,
          value === 'grid'
            ? 'border border-sky-500/20 bg-sky-500/15 text-sky-300'
            : 'text-slate-500 hover:text-white',
        ].join(' ')}
      >
        <LayoutGrid className="size-3.5" aria-hidden="true" />
      </button>

      {/* Screen reader announcement for mode changes */}
      <span
        ref={announcementRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
    </div>
  );
}

/**
 * Reads the persisted view mode from localStorage using the safe storage utility.
 * Returns 'list' as the default if:
 * - localStorage is unavailable (SSR, restricted environments)
 * - No value is stored
 * - The stored value is not a valid ClientViewMode
 *
 * @returns The persisted view mode or 'list' as fallback
 */
export function getPersistedViewMode(): ClientViewMode {
  const stored = safeStorageGet(
    typeof window !== 'undefined' ? window.localStorage : null,
    STORAGE_KEY,
  );
  if (stored && VALID_VIEW_MODES.includes(stored as ClientViewMode)) {
    return stored as ClientViewMode;
  }
  return 'list';
}

export { STORAGE_KEY };
